import { useEffect, useRef, useState } from 'react';

import { attachmentUrl, ChatAttachment, ChatSubmission, uploadChatAttachment } from 'store/services/LLMService';

export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,application/pdf,.pdf';
// Sent/history images use cookie-authenticated API URLs, so releasing draft blobs cannot break the timeline.
export const attachmentPreview = (image: ChatAttachment) => ({
	id: image.id,
	name: image.filename,
	type: image.mime_type,
	size: image.size_bytes,
	preview: attachmentUrl(image.id),
});
type DraftImage = ReturnType<typeof attachmentPreview> & { file?: File; uploaded?: ChatAttachment };

export function useImageAttachments() {
	const [files, setFiles] = useState<DraftImage[]>([]);
	const current = useRef(files);
	const urls = useRef(new Set<string>());
	const [error, setError] = useState('');
	const [uploading, setUploading] = useState(false);
	const [uploadingId, setUploadingId] = useState<number | null>(null);
	const update = (next: DraftImage[]) => {
		current.current = next;
		setFiles(next);
	};
	useEffect(
		() => () => {
			urls.current.forEach((url) => URL.revokeObjectURL(url));
		},
		[]
	);
	const remove = (id: number) => {
		const image = current.current.find((item) => item.id === id);
		if (image && urls.current.has(image.preview)) {
			URL.revokeObjectURL(image.preview);
			urls.current.delete(image.preview);
		}
		update(current.current.filter((item) => item.id !== id));
	};
	const add = (incoming: File[]) => {
		setError('');
		const next = [...current.current];
		for (const file of incoming) {
			// The server verifies the actual format, image dimensions, and PDF page count.
			const isPdf = file.type === 'application/pdf' || (!file.type && /\.pdf$/i.test(file.name));
			const type = isPdf ? 'application/pdf' : file.type;
			if (!IMAGE_ACCEPT.split(',').includes(type) || file.size > (isPdf ? 10 : 5) * 1024 * 1024) {
				setError('Use PNG, JPEG or WebP images up to 5 MiB, or PDFs up to 10 MiB and 100 pages.');
				continue;
			}
			if (next.length >= 4) {
				setError('Attach up to four files per message.');
				break;
			}
			const preview = URL.createObjectURL(file);
			urls.current.add(preview);
			next.push({ id: Date.now() + Math.random(), name: file.name, type, size: file.size, file, preview });
		}
		update(next);
	};
	const paste = (event: React.ClipboardEvent<HTMLDivElement>, allowImages = true) => {
		const incoming = Array.from(event.clipboardData.files);
		// Insert plain text ourselves so mixed image/text paste keeps text without embedding unmanaged images.
		event.preventDefault();
		if (incoming.length && allowImages) add(incoming);
		document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
	};
	const discard = (submission: ChatSubmission) => {
		const ids = new Set(submission.attachments.map((image) => image.id));
		current.current.filter((image) => image.uploaded && ids.has(image.uploaded.id)).forEach((image) => remove(image.id));
	};
	const restore = (submission: ChatSubmission) => {
		const ids = new Set(current.current.map((image) => image.uploaded?.id));
		update([
			...submission.attachments
				.filter((image) => !ids.has(image.id))
				.map((uploaded) => ({ ...attachmentPreview(uploaded), uploaded })),
			...current.current,
		]);
	};
	const submit = async (text: string): Promise<ChatSubmission> => {
		const snapshot = [...current.current];
		if (snapshot.length > 4) {
			setError('Attach up to four files per message. Remove files before retrying.');
			throw new Error('Too many attachments');
		}
		setUploading(true);
		setError('');
		try {
			const result: ChatAttachment[] = [];
			for (const image of snapshot) {
				setUploadingId(image.uploaded ? null : image.id);
				// Cache each successful upload immediately, including when a later image fails.
				const uploaded = image.uploaded ?? (await uploadChatAttachment(image.file!));
				update(current.current.map((item) => (item.id === image.id ? { ...item, uploaded } : item)));
				result.push(uploaded);
			}
			const submission = { text: text.trim(), attachments: result };
			// Only detach this snapshot; completion must never clear a newer draft.
			discard(submission);
			return submission;
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Attachment upload failed.');
			throw err;
		} finally {
			setUploadingId(null);
			setUploading(false);
		}
	};
	return {
		files,
		add,
		remove,
		paste,
		submit,
		restore,
		discard,
		uploading,
		uploadingId,
		error,
	};
}
